import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Header } from '@/components/Header';
import { Footer, FeedbackBanner } from '@/components/Footer';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { TestimonialsSection } from '@/components/TestimonialsSection';
import { Award, Users, Clock, Building2, TrendingUp, Shield, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const stats = [
  { number: '10+', label: 'Yıllık Tecrübe', icon: Clock },
  { number: '11.552+', label: 'Mutlu Müşteri', icon: Users },
  { number: '10.172+', label: 'Başarılı Onarım', icon: TrendingUp },
  { number: '%90', label: 'Başarı Oranı', icon: CheckCircle },
  { number: '1', label: 'Şube', icon: Building2 },
];

const values = [
  {
    icon: Award,
    title: 'Kalite',
    description: 'Her onarımda en yüksek kalite standartlarını uyguluyoruz.',
  },
  {
    icon: Shield,
    title: 'Güven',
    description: 'Müşteri memnuniyeti ve güven bizim için her şeyden önce gelir.',
  },
  {
    icon: Clock,
    title: 'Hız',
    description: 'Cihazınızı en kısa sürede onarıp size teslim ediyoruz.',
  },
  {
    icon: Users,
    title: 'Profesyonellik',
    description: 'Sertifikalı ve deneyimli ekibimizle kaliteli hizmet sunuyoruz.',
  },
];

const defaultAbout = {
  title: 'Teslapro Teknik Servis',
  subtitle: 'Hakkımızda',
  history: `Teslapro Teknik Servis olarak, 2014 yılında küçük bir atölyede başlayan yolculuğumuz, bugün Türkiye'nin önde gelen teknik servis noktalarından biri haline gelmiştir. İlk günden bu yana kalite, güven ve müşteri memnuniyetini ön planda tutarak, binlerce cihazı başarıyla onardık.

Sürekli gelişen teknolojiye ayak uydurarak, en son onarım tekniklerini ve ekipmanlarını kullanıyoruz. Ekibimiz düzenli olarak eğitimler alarak kendini geliştirmekte ve müşterilerimize en iyi hizmeti sunmak için çalışmaktadır.

10 yılı aşkın tecrübemizle, 11.500'den fazla mutlu müşteriye hizmet vermenin gururunu yaşıyoruz. Amacımız, teknolojinin hayatımızdaki önemini bilerek, cihazlarınızın her zaman en iyi performansı göstermesini sağlamaktır.`
};

const AboutPage = () => {
  const [content, setContent] = useState(defaultAbout);
  const [seo, setSeo] = useState({});

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [aboutRes, seoRes] = await Promise.all([
          fetch(`${API_URL}/api/content/about`),
          fetch(`${API_URL}/api/content/seo`)
        ]);
        if (aboutRes.ok) {
          const data = await aboutRes.json();
          if (data && Object.keys(data).length > 0) {
            setContent(prev => ({ ...prev, ...data }));
          }
        }
        if (seoRes.ok) {
          const data = await seoRes.json();
          if (data) setSeo(data);
        }
      } catch (e) { console.error(e); }
    };
    fetchContent();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {seo.title && (
        <Helmet>
          <title>{seo.title}</title>
          {seo.description && <meta name="description" content={seo.description} />}
          {seo.keywords && <meta name="keywords" content={seo.keywords} />}
        </Helmet>
      )}
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-hero py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3">
                  {content.subtitle || 'Hakkımızda'}
                </span>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-foreground mb-6">
                  {content.title || 'Teslapro Teknik Servis'}
                </h1>
                <p className="text-lg text-muted-foreground mb-6">
                  2014 yılından bu yana Türkiye'nin güvenilir teknik servis noktası olarak 
                  hizmet vermekteyiz. Profesyonel ekibimiz ve son teknoloji ekipmanlarımızla 
                  tüm marka cihazlarınızın onarımını garantili olarak gerçekleştiriyoruz.
                </p>
                <p className="text-muted-foreground">
                  Apple, Samsung, Xiaomi ve daha birçok markanın cihazlarına uzman servis 
                  hizmeti sunuyor, müşterilerimize her zaman en iyi deneyimi yaşatmayı 
                  hedefliyoruz.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="relative"
              >
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1576613109753-27804de2cba8?w=800&q=80"
                    alt="Teknik Servis"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground rounded-xl p-6 shadow-lg">
                  <p className="text-4xl font-bold font-display">10+</p>
                  <p className="text-sm opacity-90">Yıllık Deneyim</p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 lg:py-20 bg-foreground text-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="w-7 h-7 text-primary" />
                  </div>
                  <p className="text-3xl lg:text-4xl font-bold font-display mb-1">
                    {stat.number}
                  </p>
                  <p className="text-background/70">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Google Reviews */}
        <TestimonialsSection />

        {/* Social Media Section */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3">
                Sosyal Medya
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold font-display text-foreground mb-4">
                Bizi Takip Edin
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Güncel içeriklerimiz, onarım videoları ve kampanyalarımız için sosyal medya hesaplarımızı takip edin
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {/* YouTube */}
              <motion.a
                href="https://www.youtube.com/@teslaprotr"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="group bg-card rounded-2xl p-8 text-center border shadow-card hover:shadow-lg hover:border-red-500/50 transition-all"
              >
                <div className="w-20 h-20 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-red-500 group-hover:scale-110 transition-all">
                  <svg className="w-10 h-10 text-red-500 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold font-display text-foreground mb-2">YouTube</h3>
                <p className="text-sm text-muted-foreground">Onarım videoları ve teknik içerikler</p>
              </motion.a>

              {/* TikTok */}
              <motion.a
                href="https://www.tiktok.com/@teslaprotr"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="group bg-card rounded-2xl p-8 text-center border shadow-card hover:shadow-lg hover:border-foreground/50 transition-all"
              >
                <div className="w-20 h-20 rounded-2xl bg-foreground/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-foreground group-hover:scale-110 transition-all">
                  <svg className="w-10 h-10 text-foreground group-hover:text-background transition-colors" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold font-display text-foreground mb-2">TikTok</h3>
                <p className="text-sm text-muted-foreground">Kısa ve eğlenceli içerikler</p>
              </motion.a>

              {/* Instagram */}
              <motion.a
                href="https://www.instagram.com/teslaprotr/"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="group bg-card rounded-2xl p-8 text-center border shadow-card hover:shadow-lg hover:border-pink-500/50 transition-all"
              >
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-orange-500/10 flex items-center justify-center mx-auto mb-4 group-hover:from-purple-500 group-hover:via-pink-500 group-hover:to-orange-500 group-hover:scale-110 transition-all">
                  <svg className="w-10 h-10 text-pink-500 group-hover:text-white transition-colors" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold font-display text-foreground mb-2">Instagram</h3>
                <p className="text-sm text-muted-foreground">Güncel paylaşımlar ve hikayeler</p>
              </motion.a>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16 lg:py-24 bg-muted/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3">
                Değerlerimiz
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold font-display text-foreground mb-4">
                Bizi Farklı Kılan Ne?
              </h2>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card rounded-2xl p-6 text-center border shadow-card"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold font-display text-foreground mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Company History */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-sm font-medium text-primary uppercase tracking-wider mb-3">
                Hikayemiz
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold font-display text-foreground mb-4">
                Tecrübemiz ve Yolculuğumuz
              </h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="prose prose-lg max-w-none text-muted-foreground"
            >
              {(content.history || defaultAbout.history).split('\n\n').map((paragraph, index) => (
                <p key={index} className={`text-lg leading-relaxed ${index > 0 ? 'mt-6' : ''}`}>
                  {paragraph}
                </p>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Feedback Banner */}
        <FeedbackBanner />
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default AboutPage;
